import {
  ChangeCircleOutlined,
  ArrowDropDown,
  ArrowDropUp,
  RefreshOutlined,
} from "@mui/icons-material"
import {
  Button,
  Chip,
  MenuItem,
  Popover,
  Divider,
  Paper,
  Stack,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { Theme } from "@mui/material"
import Grid2 from "@mui/material/Unstable_Grid2"
import { useStore } from "@nanostores/react"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import type React from "react"
import { useEffect, useMemo, useState } from "react"

import { useNavigate } from "react-router-dom"

import { BurnLiquidity } from "./BurnLiquidity"
import { LiquidityTable } from "./LiquidityTable"
import { PoolDetailsCard } from "./PoolDetailsCard"
import { PoolPing } from "./PoolPing"
import { PriceCandlesChart } from "./PriceCandlesChart"
import { StatsSection } from "./StatsSection"
import { SwapsTable } from "./SwapsTable"
import { TokenHolderTable } from "./TokenHolderTable"
import { TradeOnBark } from "./TradeOnBark"
import { IdBlock } from "../../components/IdBlock"
import OnboardingPoolCTA from "../../components/OnboardingPoolCTA"
import { Subheading } from "../../components/Subheading"
import { TokenAvatar } from "../../components/TokenAvatar"
import TokenCard from "../../components/TokenCard"
import VerifiedPool from "../../components/VerifiedPool"
import { truncateId } from "../../utils/data-utils"
import { Pool } from "@/api/pools-api"
import { PoolWithFee } from "@/components/PoolWithFee"

import { PriceDisplay } from "@/components/PriceDisplay"
import ValidationCard from "@/components/TokenAudits"
import { usePool } from "@/context/PoolContext"
import { REFRESH_INTERVAL } from "@/settings"
import { $allPools, $poolsOverviewMap } from "@/stores/pools-store"
import { $forceChartRefresh } from "@/utils/tradingview-utils"

dayjs.extend(relativeTime)

export function PoolPage() {
  const { pool, loading, reversePool, baseToken, quoteToken, baseTokenInfo, quoteTokenInfo } =
    usePool()
  const queryClient = useQueryClient()

  const [lastUpdated, setLastUpdated] = useState<number | null>(new Date().getTime())
  const [updatePeriod] = useState(REFRESH_INTERVAL)
  const [activeTab, setActiveTab] = useState(0)

  const allPools = useStore($allPools)
  const overviewMap = useStore($poolsOverviewMap)

  const poolGroup = useMemo(() => {
    if (!allPools || !overviewMap) return []

    const group: Pool[] = []
    allPools.forEach((pool) => {
      if (pool.token0 === baseToken && pool.token1 === quoteToken) {
        group.push(pool)
      }
      if (pool.token1 === baseToken && pool.token0 === quoteToken) {
        group.push(pool)
      }
    })
    console.log("onPoolGroup", group)
    group.sort((a, b) => {
      return (overviewMap[a.id]?.liquidityUsd || 0) > (overviewMap[b.id]?.liquidityUsd || 0)
        ? -1
        : 1
    })
    return group
  }, [allPools, overviewMap])

  const totalLiquidityUsd = useMemo(() => {
    if (!overviewMap) return 0
    return poolGroup.reduce((acc, pool) => acc + (overviewMap[pool.id]?.liquidityUsd || 0), 0)
  }, [poolGroup])

  useEffect(() => {
    if (!pool) return

    const interval = setInterval(() => {
      handleForceRefresh()
    }, updatePeriod)

    return () => clearInterval(interval)
  }, [updatePeriod, pool])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleFlipPool = async () => {
    reversePool()
  }

  const handleForceRefresh = async (force = false) => {
    console.log("onForceRefresh", document.hasFocus())
    if (!document.hasFocus() && !force) return
    queryClient.invalidateQueries({ queryKey: ["getAllLiquidityChanges"] })
    queryClient.invalidateQueries({ queryKey: ["getAllSwaps"] })
    queryClient.invalidateQueries({ queryKey: ["Get-Token-Price"] })
    $forceChartRefresh.set($forceChartRefresh.get() + 1)
    //queryClient.invalidateQueries({ queryKey: ["createDataFeed"] })
    setLastUpdated(new Date().getTime())
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()

  // if (loading) return <LoadingSpinner />
  if (!pool) return null // This shouldn't happen due to the redirect in PoolContext

  if (pool.status === "private") {
    return <OnboardingPoolCTA />
  }

  return (
    <Grid2 container spacing={2}>
      <Grid2 xs={12}>
        <Subheading sx={isMobile ? { marginX: 0 } : undefined}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={isMobile ? 1 : 2} alignItems="center">
              <PoolPing pool={pool} />
              {pool && <VerifiedPool pool={pool} />}
              <div>
                <PoolWithFee pool={pool} />
              </div>
              <IdBlock
                label={truncateId(pool.id)}
                value={pool.id}
                href={`https://ao.link/#/entity/${pool.id}`}
                hideCopyToClipboard={isMobile}
              />
              {poolGroup.length > 1 && overviewMap && (
                <Stack direction="row" alignItems={"center"}>
                  <Button
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget)
                    }}
                    variant="contained"
                    color="secondary"
                    size="small"
                    sx={{
                      background: "var(--mui-palette-action-selected)",
                      color: "var(--mui-palette-text-primary)",
                      minWidth: "unset",
                      padding: 0.25,
                      margin: 1,
                    }}
                  >
                    {Boolean(anchorEl) ? <ArrowDropUp /> : <ArrowDropDown />}
                  </Button>
                  <Stack gap={0.25}>
                    <Typography variant="caption" color={"text.secondary"} lineHeight={1}>
                      <Typography variant="inherit" color="accent.main" component={"span"}>
                        {poolGroup.length}
                      </Typography>{" "}
                      total Pools
                    </Typography>
                    <Typography variant="caption" color={"text.secondary"} lineHeight={1}>
                      <Typography variant="inherit" color="accent.main" component={"span"}>
                        <PriceDisplay amount={totalLiquidityUsd} />
                      </Typography>{" "}
                      Liquidity
                    </Typography>
                  </Stack>
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => {
                      setAnchorEl(null)
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    BackdropProps={{
                      style: {
                        opacity: 0,
                      },
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    sx={{
                      "& .MuiPaper-root": {
                        background: "var(--mui-palette-paperAlt)",
                        border: "1px solid var(--mui-palette-divider)",
                        padding: 1,
                      },
                    }}
                  >
                    {poolGroup.map((pool) => (
                      <MenuItem
                        key={pool.id}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          navigate(`/pool/${pool.id}`)
                        }}
                      >
                        <Stack
                          direction="row"
                          gap={5}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ flex: 1 }}
                        >
                          <Stack direction="row" gap={1} alignItems="center">
                            <PoolWithFee pool={pool} />
                            <Typography variant="body2">
                              <IdBlock
                                label={truncateId(pool.id)}
                                value={pool.id}
                                hideCopyToClipboard
                              />
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color={"text.secondary"} lineHeight={1}>
                            Liquidity:{" "}
                            <Typography variant="inherit" color="accent.main" component={"span"}>
                              <PriceDisplay amount={overviewMap[pool.id]?.liquidityUsd || 0} />
                            </Typography>
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Popover>
                </Stack>
              )}

              <Tooltip title={`Last refreshed ${dayjs(lastUpdated).format("HH:MM:ss")}`}>
                <Chip
                  label={
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <RefreshOutlined /> {updatePeriod / 1000}s
                    </Stack>
                  }
                  onClick={() => handleForceRefresh()}
                  disabled={loading}
                />
              </Tooltip>
            </Stack>
          </Stack>
        </Subheading>
      </Grid2>
      <Grid2 xs={12} lg={3}>
        <Stack gap={4}>
          <TokenCard poolId={pool.id} token={baseToken} />
          <ValidationCard poolId={pool.id} tokenId={baseToken} />
          <Paper
            sx={{
              padding: 2,
              minHeight: 420,
            }}
          >
            <Stack gap={2}>
              <Stack direction="row" gap={0.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" gap={0.5} alignItems="center">
                  <TokenAvatar tokenId={baseToken} size="large" />
                  <TokenAvatar tokenId={quoteToken} size="large" />
                  <Typography variant="h5" marginLeft={1} component={Stack} direction="row">
                    <Typography component="span" variant="inherit" fontWeight={500}>
                      <IdBlock
                        label={baseTokenInfo?.ticker || truncateId(baseToken)}
                        value={baseToken}
                        href={`/token/${baseToken}`}
                        hideCopyToClipboard
                      />
                    </Typography>
                    <Typography
                      component="span"
                      color="text.secondary"
                      variant="inherit"
                      fontWeight={300}
                    >
                      /
                    </Typography>
                    <Typography
                      component="span"
                      color="text.secondary"
                      variant="inherit"
                      fontWeight={300}
                    >
                      <IdBlock
                        label={quoteTokenInfo?.ticker || truncateId(quoteToken)}
                        value={quoteToken}
                        href={`/token/${quoteToken}`}
                        hideCopyToClipboard
                      />
                    </Typography>
                  </Typography>
                  <Button onClick={handleFlipPool}>
                    <ChangeCircleOutlined />
                  </Button>
                </Stack>
              </Stack>

              <StatsSection />
              <Divider />
              <PoolDetailsCard />
            </Stack>
          </Paper>
          <TradeOnBark pool={pool} handleForceRefresh={handleForceRefresh} />
          <BurnLiquidity pool={pool} />
        </Stack>
      </Grid2>
      <Grid2 lg={9}>
        <Stack gap={2}>
          <Paper sx={{ height: 600, overflow: "hidden" }}>
            <PriceCandlesChart />
          </Paper>
          <Paper sx={{ paddingX: 2, paddingY: 1 }} component={Stack}>
            <Tabs
              value={activeTab}
              onChange={handleChange}
              sx={{
                marginBottom: 2,
                [`& .${tabClasses.root}`]: {
                  marginX: 0,
                  fontSize: "1rem",
                  fontWeight: `700 !important`,
                },
                [`& .${tabsClasses.indicator}`]: {
                  background: "var(--mui-palette-accent-main) !important",
                  height: 3,
                },
              }}
            >
              <Tab value={0} label="Transactions" />
              <Tab value={1} label="Liquidity" />
              <Tab value={2} label="Token Holders" />
              {/* <Tab value={3} label="Locked Liquidity" />
              <Tab value={4} label="Burned Liquidity" /> */}
            </Tabs>
            {activeTab === 0 && <SwapsTable />}
            {activeTab === 1 && <LiquidityTable />}
            {activeTab === 2 && <TokenHolderTable />}
            {/* {activeTab === 3 && <LockedLiquidityPanel />}
            {activeTab === 4 && <BurnedLiquidityTable />} */}
          </Paper>
        </Stack>
      </Grid2>
    </Grid2>
  )
}
